export const API_BASE = process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'production' ? 'https://marketing.northtownauto.com' : 'http://localhost:1111');

/**
 * Enhanced fetch wrapper with authentication and error handling
 */
export async function fetchJSON(endpoint, options = {}) {
  const token = localStorage.getItem("nt_token__");

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  // Add Authorization header if token exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const config = {
    method: options.method || "GET",
    cache: 'no-store',
    headers,
    ...options,
  };

  // Add body if provided
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, config);

    // Handle 401 - Unauthorized (token expired or invalid)
    if (res.status === 401) {
      console.warn("🚨 API 401 RECEIVED", {
        endpoint,
        path: window.location.pathname,
      });
      // Clear invalid token
      localStorage.removeItem("nt_token__");
      localStorage.removeItem("nt_token_exp__");

      // Redirect to login if not already there or in callback
      const path = window.location.pathname;
      if (path !== '/' && !path.includes('/logged-in')) {
        window.location.href = '/';
      }

      throw new Error("Unauthorized - please log in again");
    }

    // Handle other HTTP errors
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${res.status}: ${res.statusText}`);
    }

    return res.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: (endpoint, options) => fetchJSON(endpoint, { ...options, method: "GET" }),

  post: (endpoint, body, options) => fetchJSON(endpoint, {
    ...options,
    method: "POST",
    body
  }),

  put: (endpoint, body, options) => fetchJSON(endpoint, {
    ...options,
    method: "PUT",
    body
  }),

  patch: (endpoint, body, options) => fetchJSON(endpoint, {
    ...options,
    method: "PATCH",
    body
  }),

  delete: (endpoint, options) => fetchJSON(endpoint, {
    ...options,
    method: "DELETE"
  }),
};

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  const token = localStorage.getItem("nt_token__");
  const expStr = localStorage.getItem("nt_token_exp__");

  if (!token || !expStr) return false;

  const expiry = parseInt(expStr);
  return Date.now() < expiry;
}

/**
 * Get current user info from API
 */
export async function getCurrentUser() {
  return api.get("/api/me");
}

/**
 * Logout user (revoke session on backend)
 */
export async function logout() {
  try {
    await api.post("/api/logout");
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    // Clear local storage regardless of API response
    localStorage.removeItem("nt_token__");
    localStorage.removeItem("nt_token_exp__");
    window.location.href = '/';
  }
}

export async function postForm(endpoint, formData) {
  const token = localStorage.getItem("nt_token__");
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401) {
    localStorage.removeItem("nt_token__");
    localStorage.removeItem("nt_token_exp__");
    window.location.href = "/";
    throw new Error("Unauthorized - please log in again");
  }
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.detail || `Request failed (${response.status})`);
  return result;
}
